import { h, pageNonce } from './dom.js';

// Global arbiter: guarantees strictly ONE audio/video player is ever active across the entire app.
let activePlayer = null;

export function stopActivePlayer() {
  if (activePlayer) {
    try {
      activePlayer.stop();
    } catch {}
    activePlayer = null;
  }
}

// Plays one episode. The site hands out a short-lived address for it; hls.js plays the streams browsers cannot play on
// their own, and a browser's own player is used where it exists (Safari, iPhone) or when hls.js cannot read a stream.
let hlsReady;
function loadHls(templateBase) {
  hlsReady ??= new Promise((resolve) => {
    if (window.Hls) return resolve(window.Hls);
    const s = h('script', { src: templateBase + 'hls.min.js', nonce: pageNonce() });
    s.addEventListener('load', () => resolve(window.Hls ?? null));
    s.addEventListener('error', () => resolve(null));
    document.head.append(s);
  });
  return hlsReady;
}

export function createPlayer({ video, ctx, onEnded, onFail, onTime, onShape }) {
  let hls = null;
  let token = 0;
  let pictureTimer = null;
  let stopped = false;

  const stop = () => {
    stopped = true;
    token++; // Invalidate any in-flight play() async operations immediately
    clearTimeout(pictureTimer);
    try {
      video.pause();
    } catch {}
    if (hls) {
      try {
        hls.stopLoad();
        hls.detachMedia();
        hls.destroy();
      } catch {}
      hls = null;
    }
    video.onloadedmetadata = null;
    video.onerror = null;
    video.onended = null;
    video.ontimeupdate = null;
    video.onplaying = null;
    video.removeAttribute('src');
    try {
      video.load();
    } catch {}
    if (activePlayer === instance) {
      activePlayer = null;
    }
  };

  const instance = {
    stop,
    async play(dramaId, episode, startAt = 0) {
      // Unconditionally silence and destroy any other playing video on the website
      if (activePlayer && activePlayer !== instance) {
        try {
          activePlayer.stop();
        } catch {}
      }
      activePlayer = instance;
      stopped = false;
      stop();
      stopped = false;
      activePlayer = instance;

      const mine = ++token;
      let link;
      try {
        link = await ctx.api.post('v1/play', { drama_id: dramaId, episode });
      } catch (err) {
        if (mine === token && !stopped && activePlayer === instance) onFail?.(err.status === 404 ? 'unavailable' : 'error');
        return null;
      }
      if (mine !== token || stopped || activePlayer !== instance) return null;
      const src = ctx.api.url(link.url);
      const native = video.canPlayType('application/vnd.apple.mpegurl') !== '';

      const fail = (kind) => mine === token && !stopped && activePlayer === instance && onFail?.(kind, link);
      const startNative = () => {
        if (mine !== token || stopped || activePlayer !== instance) return;
        video.src = src;
        video.play().catch(() => {});
      };

      video.onloadedmetadata = () => {
        if (mine !== token || stopped || activePlayer !== instance) return;
        if (video.videoWidth > 0) onShape?.(video.videoWidth > video.videoHeight);
        if (startAt > 5 && Number.isFinite(video.duration) && startAt < video.duration - 5) video.currentTime = startAt;
      };
      video.onerror = () => fail('error');
      video.onended = () => {
        if (mine === token && !stopped && activePlayer === instance) onEnded?.();
      };
      video.ontimeupdate = () => {
        if (mine === token && !stopped && activePlayer === instance) onTime?.(video.currentTime, video.duration);
      };
      video.onplaying = () => {
        clearTimeout(pictureTimer);
        pictureTimer = setTimeout(() => {
          if (mine === token && !stopped && activePlayer === instance && !video.paused && video.currentTime > 1 && video.videoWidth === 0) fail('h265');
        }, 3000);
      };

      if (link.type === 'hls') {
        const Hls = await loadHls(ctx.templateBase);
        if (mine !== token || stopped || activePlayer !== instance) return link;
        if (Hls && Hls.isSupported()) {
          hls = new Hls();
          hls.on(Hls.Events.ERROR, (_e, data) => {
            if (!data.fatal || mine !== token || stopped || activePlayer !== instance) return;
            if (native) {
              try {
                hls?.stopLoad();
                hls?.detachMedia();
                hls?.destroy();
              } catch {}
              hls = null;
              startNative();
            } else fail(link.h265 ? 'h265' : 'error');
          });
          hls.loadSource(src);
          hls.attachMedia(video);
          video.play().catch(() => {});
        } else if (native) startNative();
        else fail(link.h265 ? 'h265' : 'error');
      } else {
        startNative();
      }
      return link;
    },
  };

  return instance;
}
